import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import useApplicationOwnerName from '../../hooks/useApplicationOwnerName';
import { useMenu } from '../../hooks/useMenu';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { secureAccountSsnTitle } from '../../util/helpers/onboardingContentHelpers';
import { HeadingSubtitle } from '../../components/Heading';
import Paragraph from '../../components/Paragraph/Paragraph';
import SsnForm from '../../components/Forms/SsnForm';
import themeColors from '../../assets/styles/themeColors';
import {
  // selectIsIRSRequired,
  selectIsIRSRequiredFlag,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import Link from '../../components/Link';
import VideoPlayer from '../../components/Forms/VideoPlayer';
import Modal from '../../components/Modal';
import subtitleTrack from '../../assets/subtitles/Why-SSN.vtt';
import { useCrmEvent } from '../../util/helpers/crmEventsHelper';
import { PAGES } from '../../constants/pages';
import { isIraAccount } from '../../util/helpers/iraAccountHelpers';
import LastFourSsnForm from '../../components/Forms/LastFourSsnForm';
import { APPLICANT_TYPES, APPLICATION_TYPE_STRING } from '../../util/constants';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { getIsMonWedFri } from '../../util/helpers/dateHelpers';

const SecureAccountSSN = ({ applicantType }) => {
  const { activeMenu, activeItem } = useMenu();
  const { t } = useTranslation();
  const applicantName = useApplicationOwnerName(applicantType);
  const Application = useSelector(selectApplication);
  const { ApplicationType } = Application;
  const { BrokerName } = useSelector(selectSettings);
  const formId = 'ssn_form';
  const [eventType, setEventType] = useState(null);
  const settings = useSelector(selectSettings);
  const [ssnVideoModalOpen, setSsnVideoModalOpen] = useState(false);
  const [ssnVideoUrl, setSsnVideoUrl] = useState('');
  const handleCrmEvent = useCrmEvent();
  const isIRAFlow = isIraAccount();
  const isUTMA =
    applicantType === APPLICANT_TYPES.MINOR &&
    APPLICATION_TYPE_STRING[ApplicationType] === APPLICATION_TYPE_STRING[5];
  const shouldShow4digitsFlag = useSelector(selectIsIRSRequiredFlag);
  // const shouldShow4digitsFlag = useSelector(selectIsIRSRequired);
  const isMondayWednesdayFriday = getIsMonWedFri();
  const shouldShow4digits = shouldShow4digitsFlag && !isMondayWednesdayFriday;
  const ApplicantData = useApplicantData(applicantType);
  const ssn = getApplicantData(ApplicantData, 'PersonSsn', 'Ssn');

  const shouldShowNineDigitSsnForm =
    (isIRAFlow || isUTMA || !shouldShow4digits) &&
    (!ssn?.length || ssn?.length > 4);

  useEffect(() => {
    if (settings?.SSNVideoSlug) {
      const url = `${settings?.RootUrl}${settings?.SSNVideoSlug}`;
      setSsnVideoUrl(url);
    }
  }, [settings]);

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
      formId={formId}
      setEventType={setEventType}
    >
      <Helmet>
        <title>
          {t('titleTagIdentityVerification', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {secureAccountSsnTitle(applicantType, applicantName, ApplicationType)}
      </HeadingSubtitle>
      <Paragraph color={themeColors.colorTextXLLight} fontWeight={400} mb={32}>
        {t(
          shouldShowNineDigitSsnForm
            ? 'secureIdentitySSN.subtitle'
            : 'secureIdentitySSN.last4Subtitle',
        )}
      </Paragraph>
      {shouldShowNineDigitSsnForm ? (
        <SsnForm
          applicantType={applicantType}
          nextPath={activeItem?.nextPath}
          formId={formId}
          setEventType={setEventType}
          eventType={eventType}
        />
      ) : (
        <>
          <Paragraph
            color={themeColors.colorTextPrimary}
            fontWeight={600}
            mb={16}
          >
            {t('secureIdentitySSN.last4Ssn')}
          </Paragraph>
          <LastFourSsnForm
            applicantType={applicantType}
            nextPath={activeItem?.nextPath}
            formId={formId}
            setEventType={setEventType}
            eventType={eventType}
          />
        </>
      )}
      {ssnVideoUrl && (
        <Link
          mt={16}
          textAlign="left"
          onClick={() => setSsnVideoModalOpen(!ssnVideoModalOpen)}
          title={t('videos.secureYourAccountTitle')}
          hoverTextDecoration="underline"
          color={`${themeColors.colorButtonPrimary}`}
        />
      )}
      <Modal
        variant="xl"
        isOpen={ssnVideoModalOpen}
        onClose={() => setSsnVideoModalOpen(false)}
        label={t('videos.modalSSNVideoTitle')}
        isVideoModal
        noPadding
      >
        <VideoPlayer
          url={ssnVideoUrl}
          subtitleTrack={subtitleTrack}
          handleCrmEvent={handleCrmEvent}
          pagePath={PAGES.SECURE_IDENTITY_SSN}
        />
      </Modal>
    </OnBoardingContainer>
  );
};

SecureAccountSSN.propTypes = {
  applicantType: PropTypes.string,
};

export default SecureAccountSSN;
